export type StatusMeta = {
    id: number;
    label: string;
    badgeClass: string;
};

const defaultBadgeClass = 'bg-slate-500 text-white';

const SALE_ORDER_STATUSES: Record<number, StatusMeta> = {
    1: { id: 1, label: 'Draft', badgeClass: 'bg-slate-500 text-white' },
    2: { id: 2, label: 'Confirmed', badgeClass: 'bg-blue-600 text-white' },
    3: { id: 3, label: 'In Production', badgeClass: 'bg-orange-500 text-white' },
    4: { id: 4, label: 'Ready to Ship', badgeClass: 'bg-yellow-500 text-black' },
    5: { id: 5, label: 'Dispatched', badgeClass: 'bg-purple-600 text-white' },
    6: { id: 6, label: 'Closed', badgeClass: 'bg-green-600 text-white' },
    7: { id: 7, label: 'Cancelled', badgeClass: 'bg-red-600 text-white' },
};

const PURCHASE_ORDER_STATUSES: Record<number, StatusMeta> = {
    1: { id: 1, label: 'Draft', badgeClass: 'bg-slate-600 text-white' },
    2: { id: 2, label: 'Approved', badgeClass: 'bg-blue-600 text-white' },
    3: { id: 3, label: 'Partially Received', badgeClass: 'bg-purple-600 text-white' },
    4: { id: 4, label: 'Received', badgeClass: 'bg-cyan-600 text-white' },
    5: { id: 5, label: 'Billed', badgeClass: 'bg-indigo-600 text-white' },
    6: { id: 6, label: 'Closed', badgeClass: 'bg-green-600 text-white' },
    7: { id: 7, label: 'Cancelled', badgeClass: 'bg-red-600 text-white' },
};

const GRN_STATUSES: Record<number, StatusMeta> = {
    1: { id: 1, label: 'Received', badgeClass: 'bg-cyan-100 text-cyan-800 border border-cyan-200' },
    4: { id: 4, label: 'Cancelled', badgeClass: 'bg-red-100 text-red-700 border border-red-200' },
};

const DELIVERY_STATUSES: Record<number, StatusMeta> = {
    1: { id: 1, label: 'Draft', badgeClass: 'bg-slate-100 text-slate-700 border border-slate-200' },
    2: { id: 2, label: 'Dispatched', badgeClass: 'bg-green-100 text-green-700 border border-green-200' },
    3: { id: 3, label: 'Cancelled', badgeClass: 'bg-red-100 text-red-700 border border-red-200' },
};

const INVOICE_STATUSES: Record<number, StatusMeta> = {
    1: { id: 1, label: 'Draft', badgeClass: 'bg-slate-100 text-slate-700 border border-slate-200' },
    2: { id: 2, label: 'Posted', badgeClass: 'bg-blue-100 text-blue-700 border border-blue-200' },
    3: { id: 3, label: 'Paid', badgeClass: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
    4: { id: 4, label: 'Cancelled', badgeClass: 'bg-red-100 text-red-700 border border-red-200' },
    5: { id: 5, label: 'Partially Paid', badgeClass: 'bg-amber-100 text-amber-800 border border-amber-200' },
};

const SUPPLIER_BILL_STATUSES: Record<number, StatusMeta> = {
    1: { id: 1, label: 'Pending', badgeClass: 'bg-slate-600 text-white' },
    2: { id: 2, label: 'Posted', badgeClass: 'bg-blue-600 text-white' },
    3: { id: 3, label: 'Paid', badgeClass: 'bg-green-600 text-white' },
    5: { id: 5, label: 'Partially Paid', badgeClass: 'bg-amber-500 text-black' },
    4: { id: 4, label: 'Cancelled', badgeClass: 'bg-red-600 text-white' },
};

const poNameAliases: Record<string, number> = {
    pending: 1,
    draft: 1,
    approved: 2,
    'in production': 2,
    'partially delivered': 3,
    'partially received': 3,
    received: 4,
    billed: 5,
    closed: 6,
    cancelled: 7,
    canceled: 7,
};

function normalizeName(name?: string): string {
    return (name || '').trim().toLowerCase();
}

function getMeta(map: Record<number, StatusMeta>, statusId?: number, fallbackLabel?: string): StatusMeta {
    const meta = statusId ? map[statusId] : undefined;
    if (meta) return meta;
    return { id: statusId || 0, label: fallbackLabel || 'Unknown', badgeClass: defaultBadgeClass };
}

export function getSaleOrderStatusMeta(statusId?: number, statusName?: string): StatusMeta {
    const byId = getMeta(SALE_ORDER_STATUSES, statusId, statusName || 'Draft');
    if (statusName && !SALE_ORDER_STATUSES[statusId || 0]) {
        return { ...byId, label: statusName };
    }
    return byId;
}

export function getPurchaseOrderStatusMeta(statusId?: number, statusName?: string): StatusMeta {
    let effectiveId = statusId;
    if ((!effectiveId || !PURCHASE_ORDER_STATUSES[effectiveId]) && statusName) {
        effectiveId = poNameAliases[normalizeName(statusName)];
    }
    const fallbackLabel = statusName || 'Draft';
    return getMeta(PURCHASE_ORDER_STATUSES, effectiveId, fallbackLabel);
}

export function getGrnStatusMeta(statusId?: number, statusName?: string): StatusMeta {
    const byId = getMeta(GRN_STATUSES, statusId, statusName || 'Received');
    if (statusName && !GRN_STATUSES[statusId || 0]) {
        return { ...byId, label: statusName };
    }
    return byId;
}

export function getDeliveryStatusMeta(statusId?: number): StatusMeta {
    return getMeta(DELIVERY_STATUSES, statusId, 'Draft');
}

export function getInvoiceStatusMeta(statusId?: number, statusName?: string): StatusMeta {
    const byId = getMeta(INVOICE_STATUSES, statusId, statusName || 'Draft');
    if (statusName && !INVOICE_STATUSES[statusId || 0]) {
        return { ...byId, label: statusName };
    }
    return byId;
}

export function getSupplierBillStatusMeta(statusId?: number): StatusMeta {
    return getMeta(SUPPLIER_BILL_STATUSES, statusId, 'Pending');
}

export const saleOrderStatusOptions: Array<{ value: number; label: string }> = Object.values(SALE_ORDER_STATUSES).map((s) => ({
    value: s.id,
    label: s.label,
}));

export const purchaseOrderStatusOptions: Array<{ value: number; label: string }> = Object.values(PURCHASE_ORDER_STATUSES).map((s) => ({
    value: s.id,
    label: s.label,
}));
